import { allSitters } from '../../../mocks/sitters';

export interface BookingFormData {
  service: string;
  startDate: string;
  endDate: string;
  petType: string;
  petDetails: string;
  notes: string;
  name: string;
  email: string;
}

interface ServiceOption {
  id: string;
  label: string;
  icon: string;
  priceAdd: number;
  unit: string;
  multiDay: boolean;
}

const serviceOptions: ServiceOption[] = [
  { id: 'walk', label: '犬の散歩', icon: 'ri-walk-line', priceAdd: 0, unit: '回', multiDay: false },
  { id: 'visit', label: '訪問ケア', icon: 'ri-home-heart-line', priceAdd: 5, unit: '回', multiDay: false },
  { id: 'overnight', label: 'お泊まり', icon: 'ri-hotel-bed-line', priceAdd: 25, unit: '泊', multiDay: true },
  { id: 'taxi', label: 'タクシー送迎', icon: 'ri-taxi-line', priceAdd: 8, unit: '回', multiDay: false },
];

interface Props {
  selectedSitterId: number | null;
  formData: BookingFormData;
  onChange: (data: Partial<BookingFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function ServiceDateStep({
  selectedSitterId,
  formData,
  onChange,
  onNext,
  onBack,
}: Props) {
  const sitter = allSitters.find(s => s.id === selectedSitterId);
  const selectedService = serviceOptions.find(s => s.id === formData.service);
  const basePrice = sitter?.pricePerVisit ?? 0;
  const totalPrice = selectedService ? basePrice + selectedService.priceAdd : 0;

  const isMultiDay = selectedService?.multiDay ?? false;

  const today = new Date().toISOString().slice(0, 16);

  const canProceed =
    formData.service &&
    formData.startDate &&
    formData.petType &&
    formData.petDetails.trim().length > 0 &&
    (!isMultiDay || formData.endDate);

  const inputClass =
    'w-full px-4 py-3 border border-[#D4E8EC] rounded-xl text-sm outline-none focus:border-[#81D8D0] transition-colors bg-white';

  return (
    <div>
      {sitter && (
        <div className="flex items-center gap-3 p-4 bg-[#EEF4F7] rounded-2xl mb-6">
          <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
            <img src={sitter.profileImage} alt={sitter.name} className="w-full h-full object-cover object-top" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#1B2A4A]">{sitter.name}</p>
            <p className="text-xs text-[#1B2A4A]/50">{sitter.city}, {sitter.country}</p>
          </div>
        </div>
      )}

      <h2 className="text-xl font-bold text-[#1B2A4A] mb-2">サービスと日程</h2>
      <p className="text-sm text-[#1B2A4A]/55 mb-5">希望のサービスと日程を選んでください</p>

      {/* Service Selection */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-3">
          サービスを選択 <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          {serviceOptions.filter(s =>
            !sitter || sitter.services.some(sv => sv.includes(s.label.slice(0, 2)))
          ).map(service => {
            const isSelected = formData.service === service.id;
            const price = basePrice + service.priceAdd;
            return (
              <button
                key={service.id}
                type="button"
                onClick={() => onChange({ service: service.id })}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  isSelected
                    ? 'border-[#81D8D0] bg-[#EEF4F7]'
                    : 'border-[#D4E8EC] bg-white hover:border-[#81D8D0]/50'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-[#81D8D0]' : 'bg-[#EEF4F7]'}`}>
                  <i className={`${service.icon} text-xl ${isSelected ? 'text-white' : 'text-[#81D8D0]'}`}></i>
                </div>
                <span className="text-xs font-bold text-[#1B2A4A]">{service.label}</span>
                <span className="text-sm font-black text-[#81D8D0]">€{price}<span className="text-xs font-normal text-[#1B2A4A]/40">/{service.unit}</span></span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date Selection */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-2">
          {isMultiDay ? '開始日時' : '希望日時'} <span className="text-red-400">*</span>
        </label>
        <input
          type="datetime-local"
          value={formData.startDate}
          min={today}
          onChange={e => onChange({ startDate: e.target.value })}
          className={inputClass}
        />
      </div>

      {isMultiDay && (
        <div className="mb-5">
          <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-2">
            終了日時 <span className="text-red-400">*</span>
          </label>
          <input
            type="datetime-local"
            value={formData.endDate}
            min={formData.startDate || today}
            onChange={e => onChange({ endDate: e.target.value })}
            className={inputClass}
          />
        </div>
      )}

      {/* Pet Info */}
      <div className="mb-5">
        <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-2">
          ペットの種類 <span className="text-red-400">*</span>
        </label>
        <select
          value={formData.petType}
          onChange={e => onChange({ petType: e.target.value })}
          className={`${inputClass} cursor-pointer`}
        >
          <option value="">選択してください</option>
          <option value="犬">犬</option>
          <option value="猫">猫</option>
          <option value="その他">その他</option>
        </select>
      </div>

      <div className="mb-5">
        <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-2">
          ペットの詳細 <span className="text-red-400">*</span>
        </label>
        <textarea
          value={formData.petDetails}
          onChange={e => {
            if (e.target.value.length <= 500) onChange({ petDetails: e.target.value });
          }}
          rows={3}
          maxLength={500}
          placeholder="例：ゴールデンレトリバー、3歳、オス。人懐っこく元気な性格です。"
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-[#1B2A4A]/40 text-right mt-1">{formData.petDetails.length}/500</p>
      </div>

      <div className="mb-8">
        <label className="block text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider mb-2">
          ご要望・注意事項
        </label>
        <textarea
          value={formData.notes}
          onChange={e => {
            if (e.target.value.length <= 500) onChange({ notes: e.target.value });
          }}
          rows={2}
          maxLength={500}
          placeholder="アレルギーや特別な注意事項があればご記入ください"
          className={`${inputClass} resize-none`}
        />
      </div>

      {selectedService && (
        <div className="bg-[#EEF4F7] rounded-xl p-4 mb-6 flex items-center justify-between">
          <span className="text-sm text-[#1B2A4A]/60">合計料金の目安</span>
          <span className="text-xl font-black text-[#81D8D0]">€{totalPrice}<span className="text-xs font-normal text-[#1B2A4A]/40">/{selectedService.unit}</span></span>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 py-4 bg-[#EEF4F7] text-[#1B2A4A] font-semibold rounded-xl hover:bg-[#D4E8EC] transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-2"></i>戻る
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="flex-[2] py-4 bg-[#81D8D0] text-white font-bold rounded-xl hover:bg-[#1B2A4A] transition-all disabled:bg-[#D4E8EC] disabled:cursor-not-allowed whitespace-nowrap"
        >
          次へ：お客様情報
          <i className="ri-arrow-right-line ml-2"></i>
        </button>
      </div>
    </div>
  );
}
